import { ConfigProvider, App as MyApp, Empty, theme } from "antd";
import { useMemo } from "react";
import Guard, { GuardProvider } from "./Guard";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import MasterPage from "./pages/MasterPage";
import CompaniesPage from "./pages/CompaniesPage";
import UsersPage from "./pages/UsersPage";
import AdminsPage from "./pages/AdminsPage";
import RolesPage from "./pages/RolesPage";
import CompanyProfilePage from "./pages/UserProfilePage";
import NotFoundPage from "./pages/NotFoundPage";
import ErrorBoundary from "./components/ErrorBoundary";
import { StyleProvider } from "@ant-design/cssinjs";
import { useTheme } from "./Theme";
import DashboardPage from "./pages/DashboardPage";
import IndustriesPage from "./pages/IndustriesPage";
import PoliciesPage from "./pages/PoliciesPage";
import EmployeesPage from "./pages/EmployeesPage";
import CalendarPage from "./pages/CalendarPage";
import CompanyManagersPage from "./pages/ManagersPage";
import DataLogsPage from "./pages/DataLogsPage";
import AssignmentsPage from "./pages/AssignmentsPage";
import RewardsPage from "./pages/RewardsPage";
import BadgesPage from "./pages/BadgesPage";
import BadgesAssignmentsPage from "./pages/BadgesAssignmentsPage";
import ActionLogsPage from "./pages/ActionLogsPage";
import CategoriesPage from "./pages/CategoriesPage";
import VariablesPage from "./pages/VariablesPage";
import EventsTypesPage from "./pages/EventsTypesPage";
import IncidentTypesPage from "./pages/IncidentTypesPage";
import AssetTypesPage from "./pages/AssetTypesPage";
import AssetSubtypesPage from "./pages/AssetSubtypesPage";
import LanguagesPage from "./pages/LanguagesPage";
import ProgramCategoriesPage from "./pages/ProgramCategoriesPage";
import LoginLogsPage from "./pages/LoginLogsPage";
import PointsRulesPage from "./pages/PointsRulesPage";
import PolicyAcknowledgmentsPage from "./pages/PolicyAcknowledgmentsPage";
import BundleReportsPage from "./pages/BundleReportsPage";
import CompanyReportsPage from "./pages/CompanyReportsPage";
import TrainingReportsPage from "./pages/TrainingReportsPage";
import MetricsPage from "./pages/MetricsPage";
import TemplatesPage from "./pages/TemplatesPage";
import NotificationsPage from "./pages/NotificationsPage";
import TemplatesEventsPage from "./pages/TemplatesEventsPage";
import IncidentsPage from "./pages/IncidentsPage";
import LabelsPage from "./pages/LabelsPage";
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import CollaboratorsPage from "./pages/CollaboratorsPage";
import ConsultantsPage from "./pages/ConsultantsPage";
import ReceiptsPage from "./pages/ReceiptsPage";
import { useSelector } from "./store/store";
import { authorizeSelectors } from "./store/authorize";
import MyCalendarPage from "./pages/MyCalendarPage";
import PermissionsPage from "./pages/PermissionsPage";
import PermissionTypesPage from "./pages/PermissionTypesPage";
import InvoicesPage from "./pages/InvoicesPage";
import RoadmapsPage from "./pages/RoadmapsPage";
import ProcessAssignmentsPage from "./pages/ProcessAssignmentsPage";
import WellbeingMaterialsPage from "./pages/WellbeingMaterialsPage";
import WellbeingMaterialCategoriesPage from "./pages/WellbeingMaterialCategoriesPage";
import WellbeingMaterialAssignmentsPage from "./pages/WellbeingMaterialAssignmentsPage";
import WellbeingMaterialEngagementPage from "./pages/WellbeingMaterialEngagementPage";
import BundlesPage from "./pages/BundlesPage";
import QuestionnairesPage from "./pages/QuestionnairesPage";
import BundlesAssignmentsPage from "./pages/BundlesAssignmentsPage";
import ReportsPage from "./pages/ReportsPage";
import CounsellingRequestsPage from "./pages/CounsellingRequestsPage";
import TrainingsPage from "./pages/TrainingsPage";
import BundlesTestGroupsPage from "./pages/BundlesTestGroupsPage";
import LoyaltyHistoryPage from "./pages/LoyaltyHistoryPage";
import RewardRedemptionsPage from "./pages/RewardRedemptionsPage";
import AnswersPage from "./pages/AnswersPage";
import KpisPage from "./pages/KpisPage";
import AssessmentReportsPage from "./pages/AssessmentReportsPage";
import CompanyTreePage from "./pages/CompanyTreePage";

function App() {
  const { isDark } = useTheme();
  const permissions = useSelector(authorizeSelectors.permissions);
  const isAppAdmin = useSelector(authorizeSelectors.isAppAdmin);

  // Memoised so a new theme object (and antd style recompute) isn't created on every App render.
  const themeConfig = useMemo(
    () => ({
      algorithm: isDark ? [theme.darkAlgorithm] : [theme.defaultAlgorithm],
      cssVar: true,
      token: {
        colorPrimary: "#14a098",
        colorInfo: "#14a098",
        colorSuccess: "#52c41a",
        colorWarning: "#f6a66b",
        colorError: "#f06364",
        colorLink: "#14a098",
        borderRadius: 10,
        fontFamily:
          "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      },
      components: {
        Layout: {
          bodyBg: "#f7fafb",
          headerBg: "#f7fafb",
          siderBg: "transparent",
          triggerBg: "#eef5f5",
          triggerColor: "#0d7a73",
        },
        Menu: {
          itemSelectedBg: "#d6f3f1",
          itemSelectedColor: "#0d7a73",
          itemHoverBg: "#e8f6f5",
        },
        Button: {
          primaryShadow: "0 4px 10px rgba(20, 160, 152, 0.25)",
        },
        Card: {
          borderRadiusLG: 14,
        },
      },
    }),
    [isDark],
  );

  return (
    <ConfigProvider
      // Was `() => <></>` globally — that quietly hid every empty state across
      // the whole app and made grids look broken when they had no rows. The
      // default antd Empty is unbranded; this is a soft, teal-tinted
      // "nothing here yet" that works in tables, selects, lists.
      renderEmpty={() => (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: "#64748b", fontSize: 13 }}>
              Nothing here yet
            </span>
          }
        />
      )}
      theme={themeConfig}
    >
      <StyleProvider hashPriority="low">
        <MyApp className="flex flex-auto flex-col overflow-hidden">
         <ErrorBoundary>
          <GuardProvider>
            <BrowserRouter>
              <Routes>
                <Route
                  path="login"
                  element={
                    <Guard public>
                      <LoginPage />
                    </Guard>
                  }
                />
                <Route
                  path="account/confirmemail"
                  element={<ConfirmEmailPage />}
                />
                <Route
                  path="account/resetpassword"
                  element={<ResetPasswordPage />}
                />
                <Route
                  path="/*"
                  element={
                    <Guard redirectTo="./login">
                      <MasterPage />
                    </Guard>
                  }
                >
                  {/* Root — land on dashboard */}
                  <Route index element={<Navigate to="dashboard" replace />} />
                  {/* Dashboard  */}
                  <Route path="dashboard" element={<DashboardPage />} />
                  {/* Companies */}
                  <Route
                    path="companies/grid"
                    element={
                      permissions.can("Companies", "view") ? (
                        <CompaniesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="companies/tree"
                    element={
                      permissions.can("Companies", "view") ? (
                        <CompanyTreePage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="companies/company-roles"
                    element={
                      permissions.can("CompanyRoles", "view") ? (
                        <RolesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* Users */}
                  <Route
                    path="users/all"
                    element={
                      permissions.can("PlatformUsers", "view") ? (
                        <UsersPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="users/managers"
                    element={
                      permissions.can("CompanyManagers", "view") ? (
                        <CompanyManagersPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="users/employees"
                    element={
                      permissions.can("CompanyEmployees", "view") ? (
                        <EmployeesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="users/admins"
                    element={
                      permissions.can("PlatformUsers", "view") ? (
                        <AdminsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* Affiliates */}
                  <Route
                    path="affiliates/consultants"
                    element={
                      permissions.can("PlatformConsultants", "view") ? (
                        <ConsultantsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="affiliates/collaborators"
                    element={
                      permissions.can("PlatformCollaborators", "view") ? (
                        <CollaboratorsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="affiliates/counselling-requests"
                    element={
                      permissions.can("CounsellingRequests", "view") ? (
                        <CounsellingRequestsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="affiliates/trainings"
                    element={
                      permissions.can("Trainings", "view") ? (
                        <TrainingsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="affiliates/reports"
                    element={
                      permissions.can("WellbeingReports", "view") ? (
                        <ReportsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* Policies */}
                  <Route
                    path="policies/all"
                    element={
                      permissions.can("CompanyPolicies", "view") ? (
                        <PoliciesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* PD-01 §6 — Policy Acknowledgment Monitoring. */}
                  <Route
                    path="policies/acknowledgments"
                    element={
                      permissions.can("CompanyPolicyAcknowledgments", "view") || isAppAdmin ? (
                        <PolicyAcknowledgmentsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* Calendar */}
                  <Route
                    path="calendar"
                    element={
                      permissions.can("CalendarEvents", "view") ? (
                        <CalendarPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* Actions */}
                  {/* Hide Temporary the Actions Route */}
                  {/*<Route
                    path="actions"
                    element={
                      permissions.can("actions", "view") ? (
                        <></>
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />*/}
                  {/*Rewards*/}
                  <Route
                    path="rewards/grid"
                    element={
                      permissions.can("Rewards", "view") ? (
                        <RewardsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/*Assignments*/}
                  <Route
                    path="rewards/assignments"
                    element={
                      permissions.can("RewardsAssignment", "view") ? (
                        <AssignmentsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  ></Route>
                  {/*Redemptions*/}
                  <Route
                    path="rewards/redemptions"
                    element={
                      permissions.can("RewardRedemptions", "view") ? (
                        <RewardRedemptionsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  ></Route>
                  {/*Loyalty History*/}
                  <Route
                    path="rewards/loyalty-history"
                    element={
                      permissions.can("Rewards", "view") ? (
                        <LoyaltyHistoryPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  ></Route>
                  {/* PD-01 §8 — Points Rules (LoyaltyAccrualRules). */}
                  <Route
                    path="rewards/points-rules"
                    element={
                      permissions.can("LoyaltyAccrualRules", "view") || isAppAdmin ? (
                        <PointsRulesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  ></Route>
                  {/*Badges*/}
                  <Route
                    path="badges/grid"
                    element={
                      permissions.can("Badges", "view") ? (
                        <BadgesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="badges/assigned"
                    element={
                      permissions.can("BadgesAssignment", "view") ? (
                        <BadgesAssignmentsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/*Test Actions*/}
                  {/* Hide Temporary the Tests Actions Route */}
                  {/* <Route
                    path="test-actions"
                    element={
                      permissions.can("test-actions", "view") ? (
                        <>Test Actions</>
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  /> */}
                  {/* Bundles */}
                  <Route
                    path="bundles/all"
                    element={
                      permissions.can("Bundles", "view") ? (
                        <BundlesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="bundles/assignments"
                    element={
                      permissions.can("Bundles", "view") ? (
                        <BundlesAssignmentsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="bundles/groups"
                    element={
                      permissions.can("Bundles", "view") ? (
                        <BundlesTestGroupsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/*Tests*/}
                  <Route
                    path="tests/questionnaires"
                    element={
                      permissions.can("Questionnaires", "view") ? (
                        <QuestionnairesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="tests/answers"
                    element={
                      permissions.can("QuestionnairesAnswers", "view") ? (
                        <AnswersPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />

                  {/* Reports */}
                  <Route
                    path="reports/all"
                    element={
                      permissions.can("Reports", "view") ? (
                        <AssessmentReportsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="reports/kpis"
                    element={
                      permissions.can("KPIs", "view") ? <KpisPage /> : <Navigate to={"/"} />
                    }
                  />

                  {/*Incidents*/}
                  <Route
                    path="incidents/all"
                    element={
                      permissions.can("Incidents", "view") ? (
                        <IncidentsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />

                  {/*Notifications */}
                  <Route
                    path="notifications/all"
                    element={
                      permissions.can("Notifications", "view") ? (
                        <NotificationsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="notifications/receipts"
                    element={
                      permissions.can("NotificationsRecipients", "view") ? (
                        <ReceiptsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/*Templates */}
                  <Route
                    path="templates/all"
                    element={
                      permissions.can("Templates", "view") ? (
                        <TemplatesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="templates/categories"
                    element={
                      permissions.can("TemplatesCategories", "view") ? (
                        <CategoriesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="templates/variables"
                    element={
                      permissions.can("TemplatesVariables", "view") ? (
                        <VariablesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="templates/triggers"
                    element={
                      permissions.can("TemplatesEvents", "view") ? (
                        <TemplatesEventsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/*Processes */}
                  <Route
                    path="processes"
                    element={<Navigate to={"/processes/roadmaps"} />}
                  />
                  <Route
                    path="processes/roadmaps"
                    element={
                      permissions.can("Processes", "view") ? (
                        <RoadmapsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="processes/assignments"
                    element={
                      permissions.can("ProcessesAssignment", "view") ? (
                        <ProcessAssignmentsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* WellBeing Corner */}
                  <Route
                    path="wellbeing-corner"
                    element={<Navigate to={"/wellbeing-corner/library"} />}
                  />
                  <Route
                    path="wellbeing-corner/library"
                    element={
                      permissions.can("WellbeingCorner", "view") ? (
                        <WellbeingMaterialsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="wellbeing-corner/categories"
                    element={
                      permissions.can("WellbeingCornerCategories", "view") ? (
                        <WellbeingMaterialCategoriesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="wellbeing-corner/assignments"
                    element={
                      permissions.can("WellbeingCornerAssignment", "view") ? (
                        <WellbeingMaterialAssignmentsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="wellbeing-corner/engagement"
                    element={
                      permissions.can("WellbeingCornerEngagement", "view") ? (
                        <WellbeingMaterialEngagementPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* Invoices */}
                  <Route
                    path="invoices"
                    element={
                      permissions.can("Invoices", "view") ? (
                        <InvoicesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* Configuration */}
                  <Route
                    path="configuration/permissions"
                    element={
                      permissions.can("Permissions", "view") ||
                      isAppAdmin ? (
                        <PermissionsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="configuration/permission-types"
                    element={
                      permissions.can("Permissions", "view") ||
                      isAppAdmin ? (
                        <PermissionTypesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="configuration/industries"
                    element={
                      permissions.can("CompanyDomains", "view") ? (
                        <IndustriesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="configuration/labels"
                    element={
                      permissions.can("Labels", "view") ? (
                        <LabelsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="configuration/event-types"
                    element={
                      permissions.can("CalendarEventsTypes", "view") ? (
                        <EventsTypesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="configuration/incident-types"
                    element={
                      permissions.can("IncidentTypes", "view") ? (
                        <IncidentTypesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="configuration/asset-types"
                    element={
                      permissions.can("AssetTypes", "view") ? (
                        <AssetTypesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="configuration/asset-subtypes"
                    element={
                      permissions.can("AssetSubtypes", "view") ? (
                        <AssetSubtypesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="configuration/languages"
                    element={
                      permissions.can("Languages", "view") || isAppAdmin ? (
                        <LanguagesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="configuration/program-categories"
                    element={
                      permissions.can("CompanyProgramCategories", "view") || isAppAdmin ? (
                        <ProgramCategoriesPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="reports/metrics"
                    element={
                      permissions.can("Metrics", "view") ? (
                        <MetricsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* PD-01 §10 — Bundle / Company / Training Reports */}
                  <Route
                    path="reports/bundles"
                    element={
                      permissions.can("Bundles", "view") || isAppAdmin ? (
                        <BundleReportsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="reports/company"
                    element={
                      permissions.can("Companies", "view") || isAppAdmin ? (
                        <CompanyReportsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="reports/training"
                    element={
                      permissions.can("Trainings", "view") || isAppAdmin ? (
                        <TrainingReportsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/*System Log */}
                  <Route
                    path="system-log/data-logs"
                    element={
                      permissions.can("AppAudits", "view") ? (
                        <DataLogsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="system-log/action-logs"
                    element={
                      permissions.can("ActionLogs", "view") ? (
                        <ActionLogsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  <Route
                    path="system-log/login-logs"
                    element={
                      isAppAdmin ? (
                        <LoginLogsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />
                  {/* PD-01 §21 — Message Logs. Same data as Notifications →
                      Receipts; surfaced here as the audit view. */}
                  <Route
                    path="system-log/message-logs"
                    element={
                      permissions.can("NotificationsRecipients", "view") ? (
                        <ReceiptsPage />
                      ) : (
                        <Navigate to={"/"} />
                      )
                    }
                  />

                  <Route path="my-calendar" element={<MyCalendarPage />} />

                  <Route path="profile" element={<CompanyProfilePage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </GuardProvider>
         </ErrorBoundary>
        </MyApp>
      </StyleProvider>
    </ConfigProvider>
  );
}

export default App;
